// Name autocomplete over previously used Cap names (exact "Nom Cognom"
// is what the backend validates, so keyboard navigation matters here).

import { useEffect, useMemo, useRef, useState } from 'react';

import { Input } from '../Shared/FormStyles';
import {
	NameOption,
	NameDropdown,
	NameWrapper,
	Suggestion,
} from './Factures.styles';

interface CapNameInputProps {
	value: string;
	onChange: (v: string) => void;
	suggestions: string[];
	required?: boolean;
}

export const CapNameInput = ({
	value,
	onChange,
	suggestions,
	required,
}: CapNameInputProps) => {
	const [focused, setFocused] = useState(false);
	const [active, setActive] = useState(0);
	const boxRef = useRef<HTMLDivElement | null>(null);

	const q = value.trim().toLowerCase();
	const matches = useMemo(() => {
		if (!q || q.length < 2) return [];
		const seen = new Set<string>();
		return suggestions
			.filter((s) => {
				const low = s.toLowerCase();
				if (seen.has(low)) return false;
				seen.add(low);
				return low.includes(q);
			})
			.slice(0, 8);
	}, [q, suggestions]);

	useEffect(() => {
		const close = (e: MouseEvent) => {
			if (!boxRef.current?.contains(e.target as Node)) setFocused(false);
		};
		document.addEventListener('click', close);
		return () => document.removeEventListener('click', close);
	}, []);

	return (
		<NameWrapper ref={boxRef}>
			<Input
				type="text"
				value={value}
				required={required}
				placeholder="Nom Cognom"
				onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
					onChange(e.target.value);
					setActive(0);
					setFocused(true);
				}}
				onFocus={() => setFocused(true)}
				onKeyDown={(e) => {
					if (!matches.length) return;
					if (e.key === 'ArrowDown') {
						e.preventDefault();
						setActive((a) => Math.min(a + 1, matches.length - 1));
					} else if (e.key === 'ArrowUp') {
						e.preventDefault();
						setActive((a) => Math.max(a - 1, 0));
					} else if (e.key === 'Enter' && focused && matches[active]) {
						e.preventDefault();
						onChange(matches[active]);
						setFocused(false);
					} else if (e.key === 'Escape') {
						setFocused(false);
					}
				}}
			/>
			{focused && matches.length > 0 && (
				<NameDropdown>
					{matches.map((s, i) => (
						<NameOption
							key={s}
							$active={i === active}
							onMouseDown={(e) => {
								// prevent blur before click
								e.preventDefault();
								onChange(s);
								setFocused(false);
							}}
						>
							{s}
						</NameOption>
					))}
				</NameDropdown>
			)}
			{value.trim() && (
				<Suggestion>
					Escrigueu "Nom Cognom" (mínim 2 lletres per paraula)
				</Suggestion>
			)}
		</NameWrapper>
	);
};
